import { Injectable } from "@angular/core";

export interface MobilePosition {
  coords: {
    latitude: number;
    longitude: number;
    accuracy: number;
  };
  timestamp: number;
}

export type MobileWatchPositionCallback = (position: MobilePosition | null, error?: unknown) => void;

@Injectable({ providedIn: "root" })
export class MobileGeolocationService {
  async getCurrentPosition(): Promise<MobilePosition | null> {
    return new Promise((resolve) => {
      if (!navigator.geolocation) {
        resolve(null);
        return;
      }

      navigator.geolocation.getCurrentPosition(
        (position) => resolve(this.mapPosition(position)),
        () => resolve(null),
        {
          enableHighAccuracy: true,
          timeout: 15000,
        }
      );
    });
  }

  async watchPosition(callback: MobileWatchPositionCallback): Promise<string | null> {
    if (!navigator.geolocation) {
      return null;
    }

    const watchId = navigator.geolocation.watchPosition(
      (position) => callback(this.mapPosition(position)),
      (error) => callback(null, error),
      {
        enableHighAccuracy: true,
        timeout: 15000,
        maximumAge: 5000,
      }
    );

    return String(watchId);
  }

  async clearWatch(watchId: string): Promise<void> {
    const parsedWatchId = Number.parseInt(watchId, 10);
    if (Number.isNaN(parsedWatchId)) {
      return;
    }

    try {
      navigator.geolocation.clearWatch(parsedWatchId);
    } catch {
      // Intentionally ignored - watch can already be disposed by host platform.
    }
  }

  private mapPosition(position: GeolocationPosition): MobilePosition {
    return {
      coords: {
        latitude: position.coords.latitude,
        longitude: position.coords.longitude,
        accuracy: position.coords.accuracy,
      },
      timestamp: position.timestamp,
    };
  }
}
